import { useEffect, useState } from "react";

import { RateOverrides } from "./aiPriceBook";

/**
 * ราคาที่แก้ในแท็บ "ราคา Model" (override) — store ระดับ module + บันทึกใน localStorage ของ browser
 * modal AI Usage (จาก host ใดก็ได้), dashboard, breakdown อ่านจาก store เดียวกัน; แท็บอื่นของ browser sync ผ่าน storage event
 * React 16.14 ไม่มี useSyncExternalStore → subscribe/notify เอง
 */

const AI_RATES_STORAGE_KEY = "megen.aiModelRates.v1";

export type PersistedRates = { v: 1; overrides: RateOverrides; savedAt: string };
export type AiRatesStorageStatus = "ok" | "unavailable" | "write_failed";

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

// รับเฉพาะ key `provider|model` (lower-case, ตัว model อาจมี suffix ช่วงวันที่ `@YYYY-MM-DD~YYYY-MM-DD`
// ของรายการราคาที่มี start/end date) และราคา number ≥ 0 — ที่เหลือทิ้งเงียบ ๆ (ข้อมูลใน browser เชื่อไม่ได้)
// ห้าม @ ใน provider/model → `@` ตัวแรกคือจุดเริ่ม suffix วันที่เท่านั้น ไม่ใช่ suffix ปลอมที่กลืนอยู่ในชื่อ model
const OVERRIDE_KEY_RE = /^[^|@]+\|[^|@]+(@(\d{4}-\d{2}-\d{2})?~(\d{4}-\d{2}-\d{2})?)?$/;

export const sanitizeOverrides = (raw: unknown): RateOverrides => {
  const out: RateOverrides = {};
  if (!isRecord(raw)) return out;
  Object.entries(raw).forEach(([key, value]) => {
    if (typeof key !== "string" || !OVERRIDE_KEY_RE.test(key) || !isRecord(value)) return;
    const variants: Record<string, number> = {};
    Object.entries(value).forEach(([variant, usd]) => {
      if (!variant.trim()) return;
      if (typeof usd === "number" && Number.isFinite(usd) && usd >= 0) variants[variant] = usd;
    });
    if (Object.keys(variants).length) out[key.toLowerCase()] = variants;
  });
  return out;
};

export const parsePersistedRates = (json: string | null): { overrides: RateOverrides; savedAt: string | null } => {
  if (!json) return { overrides: {}, savedAt: null };
  try {
    const parsed: unknown = JSON.parse(json);
    if (!isRecord(parsed) || parsed.v !== 1) return { overrides: {}, savedAt: null };
    const savedAt = typeof parsed.savedAt === "string" && !isNaN(new Date(parsed.savedAt).getTime()) ? parsed.savedAt : null;
    return { overrides: sanitizeOverrides(parsed.overrides), savedAt };
  } catch {
    return { overrides: {}, savedAt: null };
  }
};

export const overrideCountOf = (overrides: RateOverrides): number =>
  Object.values(overrides).reduce((sum, v) => sum + Object.keys(v).length, 0);

// localStorage อาจไม่มี (node/test) หรือแตะแล้ว throw (Safari private, iframe sandbox) → null
const getStorage = (): Storage | null => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
};

const readFromStorage = (): { overrides: RateOverrides; savedAt: string | null; status: AiRatesStorageStatus } => {
  const storage = getStorage();
  if (!storage) return { overrides: {}, savedAt: null, status: "unavailable" };
  try {
    return { ...parsePersistedRates(storage.getItem(AI_RATES_STORAGE_KEY)), status: "ok" };
  } catch {
    return { overrides: {}, savedAt: null, status: "unavailable" };
  }
};

const writeToStorage = (overrides: RateOverrides, savedAt: string | null): AiRatesStorageStatus => {
  const storage = getStorage();
  if (!storage) return "unavailable";
  try {
    if (!Object.keys(overrides).length || !savedAt) storage.removeItem(AI_RATES_STORAGE_KEY);
    else storage.setItem(AI_RATES_STORAGE_KEY, JSON.stringify({ v: 1, overrides, savedAt } as PersistedRates));
    return "ok";
  } catch {
    return "write_failed"; // quota เต็ม / ปิด storage — ค่ายังอยู่ใน memory
  }
};

// ─── Store ───────────────────────────────────────────────────────────────────

const initial = readFromStorage();
let overrides: RateOverrides = initial.overrides;
let savedAt: string | null = initial.savedAt;
let storageStatus: AiRatesStorageStatus = initial.status;
let version = 0;
const listeners = new Set<() => void>();

const notify = () => {
  version += 1;
  listeners.forEach((l) => l());
};

const commit = (next: RateOverrides) => {
  overrides = next;
  savedAt = Object.keys(next).length ? new Date().toISOString() : null;
  storageStatus = writeToStorage(next, savedAt);
  notify();
};

const isValidPrice = (usd: number): boolean => Number.isFinite(usd) && usd >= 0;

export const aiRatesStore = {
  getOverrides: (): RateOverrides => overrides,
  getVersion: (): number => version,
  getSavedAt: (): string | null => savedAt,
  getStorageStatus: (): AiRatesStorageStatus => storageStatus,

  // usd = null → ลบ override ของ variant นั้น (กลับเป็นค่าเริ่มต้น)
  setPrice: (key: string, variant: string, usd: number | null): void => {
    if (usd !== null && !isValidPrice(usd)) return;
    const current = overrides[key] || {};

    if (usd === null) {
      if (current[variant] === undefined) return;
      const rest = { ...current };
      delete rest[variant];
      const next = { ...overrides };
      if (Object.keys(rest).length) next[key] = rest;
      else delete next[key];
      commit(next);
    } else {
      if (current[variant] === usd) return;
      commit({ ...overrides, [key]: { ...current, [variant]: usd } });
    }
  },

  resetRate: (key: string): void => {
    if (!overrides[key]) return;
    const next = { ...overrides };
    delete next[key];
    commit(next);
  },

  /**
   * ลบเฉพาะบาง variant ของ key เดียว (commit ครั้งเดียว)
   * ใช้กับ model นอก price book ที่ 1 key แสดงเป็นหลายแถว (แถวละ unit) — กด "คืนค่า" ที่แถวหนึ่ง
   * ต้องไม่ลบราคาของอีกแถว
   */
  resetVariants: (key: string, variants: string[]): void => {
    const current = overrides[key];
    if (!current) return;
    const rest = { ...current };
    let changed = false;
    variants.forEach((variant) => {
      if (rest[variant] !== undefined) {
        delete rest[variant];
        changed = true;
      }
    });
    if (!changed) return;
    const next = { ...overrides };
    if (Object.keys(rest).length) next[key] = rest;
    else delete next[key];
    commit(next);
  },

  resetAll: (): void => {
    if (!Object.keys(overrides).length) return;
    commit({});
  },

  // แท็บอื่นของ browser แก้ราคา → รับค่าใหม่โดยไม่เขียนกลับ (กัน echo)
  applyStorageEvent: (newValue: string | null): void => {
    const parsed = parsePersistedRates(newValue);
    overrides = parsed.overrides;
    savedAt = parsed.savedAt;
    notify();
  },

  // อ่าน storage ใหม่ (ใช้ใน test / debug)
  reload: (): void => {
    const next = readFromStorage();
    overrides = next.overrides;
    savedAt = next.savedAt;
    storageStatus = next.status;
    notify();
  },

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === null || e.key === AI_RATES_STORAGE_KEY) aiRatesStore.applyStorageEvent(e.key === null ? null : e.newValue);
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export type AiModelRatesApi = {
  version: number; // ใส่ใน deps ของ useMemo ที่คำนวณ cost
  overrides: RateOverrides;
  setPrice: (key: string, variant: string, usd: number | null) => void;
  resetRate: (key: string) => void;
  resetVariants: (key: string, variants: string[]) => void;
  resetAll: () => void;
  isOverridden: (key: string, variant: string) => boolean;
  hasOverrides: boolean;
  overrideCount: number;
  savedAt: string | null;
  storageStatus: AiRatesStorageStatus;
};

export const useAiModelRates = (): AiModelRatesApi => {
  const [v, setV] = useState(aiRatesStore.getVersion());

  useEffect(() => {
    const unsubscribe = aiRatesStore.subscribe(() => setV(aiRatesStore.getVersion()));
    setV(aiRatesStore.getVersion()); // กันพลาด update ที่เกิดระหว่าง render แรกกับ subscribe
    return unsubscribe;
  }, []);

  const current = aiRatesStore.getOverrides();

  return {
    version: v,
    overrides: current,
    setPrice: aiRatesStore.setPrice,
    resetRate: aiRatesStore.resetRate,
    resetVariants: aiRatesStore.resetVariants,
    resetAll: aiRatesStore.resetAll,
    isOverridden: (key, variant) => current[key]?.[variant] !== undefined,
    hasOverrides: Object.keys(current).length > 0,
    overrideCount: overrideCountOf(current),
    savedAt: aiRatesStore.getSavedAt(),
    storageStatus: aiRatesStore.getStorageStatus(),
  };
};
